import { configs } from '@/configs'
import { useAuthUser } from '@/hooks/useAuth/zustand'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { devLog } from './logs'

type Heap = {
  track(str: string, props: Record<string, any>): void
  identify(str: string): void
  resetIdentity(): void
  addUserProperties(props: Record<string, any>): void
}

declare global {
  interface Window {
    heap: Heap
  }
}

export const HeapScript = ({
  id = configs.HEAP_ANALYTICS_ID,
  onReady,
}: {
  id?: string
  onReady?: () => void | null
}) => {
  return (
    <Script id="heap" strategy="lazyOnload" onReady={onReady}>
      {`window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};   
    heap.load("${id}");`}
    </Script>
  )
}

const getHeap = async (): Promise<Heap> => {
  if (!window.heap) {
    await new Promise((resolve) => setTimeout(resolve, 1000))
    return getHeap()
  }
  return window.heap
}

export async function trackHeap({
  name,
  properties = {},
}: {
  name: string
  properties?: Record<string, string>
}) {
  const heap = await getHeap()

  try {
    heap.track(name, properties)
  } catch (err) {
    devLog(`[Heap analytics]: Error tracking ${name}`, { err })
    setTimeout(() => {
      trackHeap({ name, properties })
    }, 250)
  }
}

export default function Analytics() {
  const user = useAuthUser()

  const handleIdentify = async () => {
    const heap = await getHeap()

    if (user?.id) {
      devLog(`[Heap analytics]: Identified as ${user.email}`)
      heap.identify(user.email)
      heap.addUserProperties(user)
    } else {
      devLog(`[Heap analytics]: resetIdentity`)
      heap.resetIdentity()
    }
  }

  useEffect(() => {
    handleIdentify()
  }, [user])

  return (
    <>
      <HeapScript />
    </>
  )
}
